/**
 * Your own account.
 *
 * Changing your own password needs no account permission at all — only your
 * current password. It is on both surfaces because you should never have to
 * go somewhere else to do it.
 */
import { useState, type FormEvent } from "react";
import { ApiError, api } from "../../lib/api.ts";
import { describeAccess, useAuth } from "../../lib/auth.tsx";
import { AREA_DESCRIPTIONS, allows, type Capability } from "@dp/shared";
import { PageHead } from "../../components/Page.tsx";

const CAPABILITY_WORDS: Record<Capability, string> = { view: "see", edit: "change", delete: "remove" };

export function MyAccount() {
  const { user, signOut } = useAuth();
  const [currentPassword, setCurrentPassword] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [confirmation, setConfirmation] = useState("");
  const [problem, setProblem] = useState<string | null>(null);
  const [changed, setChanged] = useState(false);
  const [busy, setBusy] = useState(false);

  if (!user) return null;

  async function submit(event: FormEvent) {
    event.preventDefault();
    setProblem(null);

    if (newPassword !== confirmation) {
      setProblem("The two new passwords do not match.");
      return;
    }

    setBusy(true);
    try {
      await api.post("/api/auth/password", { currentPassword, newPassword });
      setChanged(true);
      // Changing a password ends every session, including this one. Saying so
      // and signing out beats letting the next click fail mysteriously.
      setTimeout(() => void signOut(), 2500);
    } catch (error) {
      setProblem(error instanceof ApiError ? error.message : "Could not change your password.");
      setBusy(false);
    }
  }

  return (
    <div className="stack">
      <PageHead title="Your account" sub={user.email} />

      <div className="card">
        <div className="card-head"><h2>What you can do</h2></div>
        <div className="card-body stack-sm">
          <div className="row">
            <span className="chip chip-accent">{describeAccess(user)}</span>
          </div>
          <ul className="access-list">
            {AREA_DESCRIPTIONS.map((description) => {
              const held = (["view", "edit", "delete"] as Capability[]).filter((capability) =>
                allows(user, description.area, capability),
              );
              return (
                <li key={description.area}>
                  <strong>{description.label}:</strong>{" "}
                  {held.length === 0
                    ? <span className="muted">no access</span>
                    : <span>you can {held.map((capability) => CAPABILITY_WORDS[capability]).join(", ")} it</span>}
                </li>
              );
            })}
          </ul>
          <p className="small muted">An administrator can change any of this.</p>
        </div>
      </div>

      <form className="card" onSubmit={submit}>
        <div className="card-head"><h2>Change your password</h2></div>
        <div className="card-body stack">
          {problem && <div className="notice notice-bad">{problem}</div>}
          {changed && (
            <div className="notice notice-ok">
              Changed. That signs you out everywhere, including here — taking you to the sign-in page now.
            </div>
          )}

          <div className="field">
            <label htmlFor="current">Current password</label>
            <input id="current" className="control" type="password" autoComplete="current-password"
              value={currentPassword} required disabled={changed}
              onChange={(event) => setCurrentPassword(event.target.value)} />
          </div>

          <div className="field-row">
            <div className="field">
              <label htmlFor="new">New password</label>
              <input id="new" className="control" type="password" autoComplete="new-password" minLength={12}
                value={newPassword} required disabled={changed}
                onChange={(event) => setNewPassword(event.target.value)} />
              <span className="hint">At least 12 characters. Length is what matters most.</span>
            </div>
            <div className="field">
              <label htmlFor="confirm">New password again</label>
              <input id="confirm" className="control" type="password" autoComplete="new-password" minLength={12}
                value={confirmation} required disabled={changed}
                onChange={(event) => setConfirmation(event.target.value)} />
            </div>
          </div>
        </div>
        <div className="card-foot">
          <button className="btn btn-primary" type="submit" disabled={busy || changed}>
            {busy ? "Changing…" : "Change password"}
          </button>
        </div>
      </form>
    </div>
  );
}
